import React, { StrictMode, Suspense, lazy } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/react'
import type { Flags } from '@fs/zion-flags'
import { setAppLocales, essentialLocales } from '@fs/zion-locale'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router } from '@fs/zion-router'
import { appPath, mergeSentryConfig, sentryDSN } from '@fs/zion-config'
import { AppLayout, colors } from '@fs/zion-ui'
import App from './App'
import * as serviceWorker from './serviceWorker'
import AncestorsHeader from './components/AncestorsHeader/AncestorsHeader'
import runQualtricsInterceptIIFE from './qualtrics-intercept'
import './locales'
import { useDataContext } from './services/data-service'

const devEnv = process.env.NODE_ENV === 'development'

// Filter out aria-description warning in development environment
// This warning is outside our control and will be fixed when we upgrade to React 18
// https://familysearch.slack.com/archives/CMLL2NGUF/p1745320619906479
// https://familysearch.slack.com/archives/CMLL2NGUF/p1759247106553329?thread_ts=1759246670.030479&cid=CMLL2NGUF
if (devEnv) {
  const originalError = console.error

  console.error = (...args) => {
    const firstArg = typeof args[0] === 'string' ? args[0] : ''
    if (
      firstArg.includes('aria-description') ||
      (firstArg.includes('Invalid aria prop') && args[1] === '`aria-description`')
    ) {
      return
    }
    originalError.apply(console, args) // Show all other errors normally
  }
}

const EmptyLoading = (): JSX.Element => <div />

const AncestorsFooter = lazy(() => import('./components/AncestorsFooter/AncestorsFooter'))

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = (devEnv ? import('./dev.flags').then((m) => m.default) : undefined) as undefined | Flags

const mockActivities = (devEnv ? import('./dev.activities').then((m) => m.default) : undefined) as undefined | Flags

/* Sentry initialization.
 * Sentry is disabled locally by default (SENTRY_DSN is unset and SENTRY_DISABLED=true in .env).
 * Snow runs locally via setupProxy.js and populates window.SERVER_DATA on every page load, so to enable Sentry in development, run:
 * SENTRY_DSN=<your-dsn> SENTRY_DISABLED=false npm start
 * Events will log under the "local" environment (TARGET_ENV in .env).
 * Additional debugging is gated conditionally by devEnv.
 */
if (sentryDSN) {
  /* Sample rates and strict filtering are hardcoded because CRA bakes REACT_APP_* env vars at BUILD time via webpack DefinePlugin — they cannot be changed at runtime via server config. To adjust these values, change them here and redeploy. */
  const sentryConfig = mergeSentryConfig({
    attachStacktrace: devEnv,
    debug: devEnv, // Log Sentry SDK diagnostics to the console in development.
    sampleRate: devEnv ? 1 : 0.01, // 0.0–1.0 fraction. 100% of events locally.
    strictFiltering: true, // Enhanced filtering via @fs/zion-config (https://github.com/fs-webdev/zion/pull/13771)
  })

  // eslint-disable-next-line no-console -- dev-locked debugging statement of value to the *browser* console.
  devEnv && console.log('Sentry config:', sentryConfig)

  Sentry.init(sentryConfig)
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

const state = window.SERVER_DATA?.ssrState

// Add any app logic here, languages and translations are configured and available here
const FrontierWrapper = (): JSX.Element => {
  setAppLocales(essentialLocales)
  const {
    store: { CF = false },
  } = useDataContext()

  return (
    <Router basename={base}>
      <Root analytics header={{ hidden: true }} footer={{ hidden: CF, external: true }} theme="day" helpChatbot={!CF}>
        <AppLayout fullWidth backgroundColor={colors.gray03}>
          <section style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <AncestorsHeader />
            <App pid={state?.pid} initialData={state?.initialData} />
          </section>
          <Suspense fallback="">{CF && <AncestorsFooter />}</Suspense>
        </AppLayout>
      </Root>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = (): JSX.Element => {
  return (
    <>
      <StrictMode>
        <FrontierBaseProviders mockFlags={mockFlags} LoadingFallback={EmptyLoading} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </StrictMode>
      <div id="ZN_cI4OucMtvbHUxSZ" />
    </>
  )
}

if (state) {
  ReactDOM.hydrate(<FrontierRoot />, document.getElementById('root'))
} else {
  ReactDOM.render(
    <Suspense fallback="">
      <FrontierRoot />
    </Suspense>,
    document.getElementById('root')
  )
}

// Inject Qualtrics Site Intercept Website Feedback Code
// NOTE: the page must somewhere have a rendered <div id='ZN_cI4OucMtvbHUxSZ' /> for code injection to work, see above.
runQualtricsInterceptIIFE()

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
